import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { useBeforeUnload } from '../../../../hooks';
import { makePatch } from '../../../../api/ajax';
import { Field } from '../../../../components/Form';
import { Modal, onHideHandler, renderShowConfirmationModal } from '../../../../components';
import { selectors, actions } from '../../../../redux/customerConfigurations';

// The API prefixes validation errors with the offending field name, e.g.
// "InslyTenantId: HTML or markup is not allowed in this field." Map that prefix
// to the Formik field so the error is shown under the correct input.
const FIELD_BY_ERROR_PREFIX = {
  webhookurl: 'webhookUrl',
  inslytenantid: 'inslyTenantId',
  scriveuseremail: 'scriveUserEmail',
  scriveuserpassword: 'scriveUserPassword',
};

const AccountIntegrationSettingsModal = ({ onModalClose, ...props }) => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useBeforeUnload(hasUnsavedChanges);

  const dispatch = useDispatch();
  const customerConfiguration = useSelector(selectors.selectCustomerConfiguration);

  const initialValues = {
    webhookUrl: customerConfiguration?.webhookUrl || '',
    inslyTenantId: customerConfiguration?.inslyTenantId || '',
    scriveUserEmail: customerConfiguration?.scriveUserEmail || '',
    scriveUserPassword: ''
  };

  const handleSubmit = (data, { setStatus, setFieldError, resetForm }) => {
    // Only send the Scrive password when the user actually entered one, otherwise
    // saving unrelated integration fields would overwrite the stored password with an empty value.
    const { scriveUserPassword, ...rest } = data;
    const payload = scriveUserPassword ? { ...rest, scriveUserPassword } : rest;

    makePatch('customerConfigurations/current', payload).subscribe(
      ({ response }) => {
        dispatch(actions.loadCurrentCustomerConfigurationSuccess(response));
        setShowSuccessMessage('Integration settings have been changed!');
        resetForm();

        setHasUnsavedChanges(false);
      },
      (error) => {
        const message = error?.response?.error || 'Failed to save integration settings. Please try again.';
        const prefix = typeof message === 'string' ? message.split(':')[0].trim().toLowerCase() : '';
        const field = FIELD_BY_ERROR_PREFIX[prefix];

        // Route the error to the field it belongs to; otherwise show it as a general error.
        if (field) {
          setFieldError(field, message);
        } else {
          setStatus(message);
        }

        setHasUnsavedChanges(false);
      }
    );
  };

  const hideDiscardConfirmation = () => {
    onModalClose();
    setShowConfirmModal(false);
  };

  return (
    <>
      {renderShowConfirmationModal(showConfirmModal, setShowConfirmModal, hideDiscardConfirmation)}
      <Modal
        headerText="Change integration settings"
        size="md"
        backdrop="static"
        onHide={() => onHideHandler(hasUnsavedChanges, onModalClose, setShowConfirmModal)}
        {...props}
      >
        <Modal.Body>
          <div className="row">
            <div className="col">
              {showSuccessMessage && (
                <Alert variant="success" dismissible onClick={() => setShowSuccessMessage(null)}>
                  {showSuccessMessage}
                </Alert>
              )}
            </div>
          </div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
              webhookUrl: Yup.string(),
              inslyTenantId: Yup.string(),
              scriveUserEmail: Yup.string().email('Please enter a correct email').nullable().notRequired(),
              scriveUserPassword: Yup.string().min(8, 'Must be at least 8 characters long').nullable().notRequired(),
            })}
            onSubmit={(values, formikHelpers) => handleSubmit(values, formikHelpers)}
            validateOnChange={false}
          >
            {({ errors, status, touched }) => (
              <Form
                onChange={({ target }) => {
                  if (!hasUnsavedChanges && target.value !== initialValues[target.name]) {
                    setHasUnsavedChanges(true);
                  }
                }}
                autoComplete="off"
              >
                {status && (
                  <div className="row">
                    <div className="col-12">
                      <Alert variant="danger">{status}</Alert>
                    </div>
                  </div>
                )}
                <div className="row">
                  <div className="col-12">
                    <Field
                      label="Webhook endpoint URL"
                      type="text"
                      name="webhookUrl"
                      touched={touched.webhookUrl}
                      errors={errors.webhookUrl}
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-12">
                    <Field
                      label="Insly tenant id"
                      type="text"
                      name="inslyTenantId"
                      touched={touched.inslyTenantId}
                      errors={errors.inslyTenantId}
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-12">
                    <Field
                      label="Scrive user email"
                      type="email"
                      name="scriveUserEmail"
                      touched={touched.scriveUserEmail}
                      errors={errors.scriveUserEmail}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-12">
                    <Field
                      label="Scrive user password"
                      type="password"
                      name="scriveUserPassword"
                      touched={touched.scriveUserPassword}
                      errors={errors.scriveUserPassword}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col-12">
                    <button
                      type="reset"
                      className="btn btn-secondary mr-1"
                      onClick={() => onHideHandler(hasUnsavedChanges, onModalClose, setShowConfirmModal)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Save
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

AccountIntegrationSettingsModal.propTypes = {
  onModalClose: PropTypes.func
};

export default AccountIntegrationSettingsModal;
